import { useMutation } from '@tanstack/react-query';
import React, { useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';

import { Modal, ModalSize } from '@column/column-ui-kit';

import { CodeInput, NotificationList } from '~/components';
import { FactorTypeResponse, UserRepository } from '~/repositories';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';
import { Hint, Inner } from '~/styles';
import { log } from '~/util';

const StyledHint = styled(Hint)`
  text-align: center;
  margin-top: 16px;
`;

// The notification slot reserves a bottom margin for when errors appear. In this
// compact modal that leaves an oversized gap above the code field while empty, so
// we drop the reserved margin and let the grid gap provide the spacing; notifications
// still expand into the slot (via ToggleHeight) with their own spacing when present.
const ModalNotifications = styled(NotificationList)`
  && {
    margin: 0;
  }
`;

const Resend = styled.span`
  display: inline-block;
  cursor: pointer;
  color: ${({ theme }) => theme.primary.background};
`;

export type AuthenticateModalProps = {
  isOpen?: boolean;
  onClose?: () => void;
};

export const AuthenticateModal = ({ isOpen, onClose }: AuthenticateModalProps) => {
  const { currentUser, authRequired, refetchCurrentUser, signIn, setMfaStatus } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();

  const pendingSmsFactor = currentUser?.factors.find(
    (factor) => !factor.factorVerified && factor.factorType === FactorTypeResponse.SMS
  );
  const [codeInput, setCodeInput] = useState<string>('');

  useEffect(() => {
    if (isOpen) {
      setCodeInput('');
    }
  }, [isOpen]);

  const handleResend = (event: React.MouseEvent) => {
    event.preventDefault();

    const { factorId } = pendingSmsFactor || {};
    if (!factorId) {
      return;
    }

    UserRepository.resendFactorVerification({ factorId })
      .then(() => {
        log({
          name: 'User MFA resent',
          context: {
            phoneNumber: currentUser?.phoneNumber,
          },
        });

        addSuccessNotification({
          content: 'Code resent',
          display: 'popup',
        });
      })
      .catch((error) => {
        addDangerNotification({
          content: error.message,
          display: 'popup',
        });
      });
  };

  const verifyMfaMutation = useMutation({
    mutationFn: async ({ factorId, code }: { factorId: string; code: string }) =>
      UserRepository.verifyMfa({ factorId, code }),
    onError: (error: unknown) => {
      addDangerNotification({
        content: error instanceof Error ? error.message : 'Failed to verify. Please try again.',
        display: 'popup',
      });
      setCodeInput('');
    },
    onSuccess: async (resp) => {
      setCodeInput('');
      setMfaStatus(resp.mfaStatus);

      if (authRequired()) {
        try {
          await signIn();
          await refetchCurrentUser();
          onClose?.();
        } catch (error) {
          addDangerNotification({
            content: error instanceof Error ? error.message : 'An error occurred',
            display: 'popup',
          });
        }
      } else {
        log({
          name: 'User enabled MFA',
          context: currentUser,
        });
        addSuccessNotification({
          content: 'Two-factor authentication enabled',
        });
        await refetchCurrentUser();
        onClose?.();
      }
    },
  });

  const handleSubmit = useCallback(() => {
    if (!currentUser || !pendingSmsFactor?.factorId || verifyMfaMutation.isPending) {
      return;
    }

    verifyMfaMutation.mutate({
      factorId: pendingSmsFactor.factorId,
      code: codeInput,
    });
  }, [currentUser, pendingSmsFactor?.factorId, verifyMfaMutation, codeInput]);

  useEffect(() => {
    if (codeInput && codeInput.length === 6) {
      handleSubmit();
    }
  }, [codeInput, handleSubmit]);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={ModalSize.FitContent}>
      <div style={{ maxWidth: 325 }}>
        <Modal.IconCircle icon="Phone" type="primary" />

        <Modal.Content align="center">
          <Modal.Headline fullWidth>Authenticate</Modal.Headline>
          <Modal.Paragraph fullWidth>Please enter the 6-digit code we have sent to your phone.</Modal.Paragraph>
          <ModalNotifications display="popup" />
          <CodeInput
            placeholder="######"
            type="number"
            value={codeInput}
            onChange={(value: string) => setCodeInput(value)}
            isLoading={verifyMfaMutation.isPending}
          />
          <Inner p={0} pb={24}>
            <StyledHint>
              Haven't received the code? <Resend onClick={handleResend}>Resend</Resend>
            </StyledHint>
          </Inner>
        </Modal.Content>
      </div>
    </Modal>
  );
};
